import imageCompression from "browser-image-compression";

// Maximum file size: 3MB
const MAX_FILE_SIZE = 3 * 1024 * 1024;

// Compression options
const compressionOptions = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
};

// Custom modal for file size warning
const showFileSizeModal = (fileSize) => {
  return new Promise((resolve) => {
    // Create modal overlay
    const overlay = document.createElement("div");
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      font-family: "Instrument Serif", serif;
    `;

    // Create modal content
    const modal = document.createElement("div");
    modal.style.cssText = `
      background: #ffffff;
      border-radius: 24px;
      padding: 40px;
      max-width: 400px;
      width: 90%;
      box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
      text-align: center;
    `;

    modal.innerHTML = `
      <h2 style="margin: 0 0 20px 0; font-size: 28px; font-weight: 600; color: #000;">
        file too large
      </h2>
      <p style="margin: 0 0 30px 0; font-size: 18px; color: #000; line-height: 1.6;">
        your file (${fileSize}mb) exceeds the 3mb limit.
        <br><br>
        would you like to compress it automatically?
      </p>
      <div style="display: flex; gap: 15px; justify-content: center;">
        <button id="modal-cancel" style="
          padding: 14px 28px;
          font-size: 16px;
          border: 1px solid #ddd;
          background: #fff;
          border-radius: 12px;
          cursor: pointer;
          font-family: inherit;
          transition: background 0.2s;
        ">cancel</button>
        <button id="modal-compress" style="
          padding: 14px 28px;
          font-size: 16px;
          border: none;
          background: #000;
          color: #fff;
          border-radius: 12px;
          cursor: pointer;
          font-family: inherit;
          transition: background 0.2s;
        ">compress</button>
      </div>
    `;

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Handle button clicks
    const compressBtn = modal.querySelector("#modal-compress");
    const cancelBtn = modal.querySelector("#modal-cancel");

    const cleanup = () => {
      document.body.removeChild(overlay);
    };

    compressBtn.onclick = () => {
      cleanup();
      resolve(true);
    };

    cancelBtn.onclick = () => {
      cleanup();
      resolve(false);
    };

    // Close on overlay click
    overlay.onclick = (e) => {
      if (e.target === overlay) {
        cleanup();
        resolve(false);
      }
    };
  });
};

// Simple MIME type to extension mapping
const mimeToExtension = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/svg+xml": "svg",
  "image/webp": "webp",
  "image/bmp": "bmp",
  "image/tiff": "tiff",
};

const localDownloadFile = (file, fileName) => {
  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(file, fileName);
  } else {
    let link = document.createElement("a");
    link.download = fileName;
    link.href = window.URL.createObjectURL(file);
    link.dispatchEvent(new MouseEvent("click"));
  }
};

const downloadFile = (file, fileName) => {
  if (file) {
    if (file instanceof File || file instanceof Blob) {
      localDownloadFile(file, fileName);
    } else if (typeof file === "string") {
      fetch(file)
        .then((res) => {
          res.blob().then((blob) => {
            localDownloadFile(blob);
          });
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      console.error("FILE IS NOT FILE OR STRING");
    }
  }
};

const getExtension = (fileOrUrl) => {
  if (fileOrUrl) {
    if (fileOrUrl instanceof File || fileOrUrl instanceof Blob) {
      if (fileOrUrl.type) {
        return mimeToExtension[fileOrUrl.type] || "";
      } else {
        return "";
      }
    } else {
      let index = fileOrUrl.lastIndexOf(".");
      if (index !== -1) {
        let ext = fileOrUrl.slice(index + 1);
        let qMarkIndex = ext.indexOf("?");
        if (qMarkIndex !== -1) {
          return ext.slice(0, qMarkIndex);
        }
        return ext;
      }
    }
  }
  return "";
};

const getUploadedImage = (callback) => {
  getUploadedImageAsBlob(({ blob, error }) => {
    if (blob) {
      let reader = new FileReader();
      reader.onload = () => {
        let img = new Image();
        img.onload = () => {
          const { naturalWidth, naturalHeight } = img;
          let aspectRatio = naturalWidth / naturalHeight;
          callback({ result: reader.result, aspectRatio });
        };
        img.onerror = () => {
          callback({ error: "Error processing image for moodboard" });
        };
        img.src = reader.result;
      };
      reader.onerror = (e) => {
        console.log(e);
        callback({ error: "error getting local uploaded image" });
      };

      reader.readAsDataURL(blob);
    } else {
      callback({ error });
    }
  });
};

// Get multiple uploaded images
const getUploadedImages = (callback) => {
  getMultipleBlobsFromUser({
    accept: ".png,.jpg,.jpeg,.svg,.gif",
    callback: async (results) => {
      if (results.error) {
        callback({ error: results.error });
        return;
      }

      const processedImages = [];
      for (const blob of results.blobs) {
        try {
          const imageData = await new Promise((resolve) => {
            let reader = new FileReader();
            reader.onload = () => {
              let img = new Image();
              img.onload = () => {
                const { naturalWidth, naturalHeight } = img;
                let aspectRatio = naturalWidth / naturalHeight;
                resolve({ result: reader.result, aspectRatio });
              };
              img.onerror = () => {
                resolve({ error: "Error processing image" });
              };
              img.src = reader.result;
            };
            reader.onerror = () => {
              resolve({ error: "Error reading image" });
            };
            reader.readAsDataURL(blob);
          });
          if (imageData.result) {
            processedImages.push(imageData);
          }
        } catch (e) {
          console.log("Error processing file:", e);
        }
      }
      callback({ images: processedImages });
    },
  });
};

const getUploadedImageAsBlob = (callback) =>
  getBlobFromUser({ accept: ".png,.jpg,.jpeg,.svg,.gif", callback });

const getBlobFromUser = ({ accept, callback }) => {
  let inputEl = document.createElement("input");
  inputEl.type = "file";
  if (accept) inputEl.accept = accept;
  inputEl.onchange = async ({ target }) => {
    if (target.files.length) {
      let blob = target.files[0];

      // Check file size - show warning modal if exceeds 3MB
      if (blob.size > MAX_FILE_SIZE) {
        const fileSizeMB = (blob.size / 1024 / 1024).toFixed(1);
        const userChoice = await showFileSizeModal(fileSizeMB);

        if (!userChoice) {
          callback({ error: "Please choose a smaller image." });
          return;
        }
      }

      // Compress image if it's not a GIF (GIFs lose animation when compressed)
      const isGif = blob.type === "image/gif";

      if (!isGif) {
        try {
          const compressedBlob = await imageCompression(
            blob,
            compressionOptions
          );
          callback({ blob: compressedBlob });
        } catch (compressionError) {
          console.log("Compression failed, using original:", compressionError);
          callback({ blob });
        }
      } else {
        // For GIFs, check size and warn if too large
        if (blob.size > MAX_FILE_SIZE) {
          callback({
            error:
              "GIF file exceeds 3MB limit. GIFs cannot be compressed. Please choose a smaller GIF.",
          });
          return;
        }
        callback({ blob });
      }
    } else {
      callback({ error: "no files" });
    }
  };

  inputEl.onclose = () => {
    console.log("close");
  };

  inputEl.oncancel = () => {
    callback({ error: "Upload cancelled" });
  };

  inputEl.onerror = () => {
    callback({ error: "error" });
  };

  // Note: onblur removed - it fires immediately on iOS when photo picker opens,
  // causing uploads to fail before user can select a file

  inputEl.click();
};

// Get multiple files from user
const getMultipleBlobsFromUser = ({ accept, callback }) => {
  let inputEl = document.createElement("input");
  inputEl.type = "file";
  inputEl.multiple = true;
  if (accept) inputEl.accept = accept;
  inputEl.onchange = async ({ target }) => {
    if (target.files.length) {
      const processedBlobs = [];

      for (let i = 0; i < target.files.length; i++) {
        let blob = target.files[i];

        // Check file size - show warning modal if exceeds 3MB
        if (blob.size > MAX_FILE_SIZE) {
          const fileSizeMB = (blob.size / 1024 / 1024).toFixed(1);
          const userChoice = await showFileSizeModal(fileSizeMB);

          if (!userChoice) {
            continue; // Skip this file
          }
        }

        // Compress image if it's not a GIF
        const isGif = blob.type === "image/gif";

        if (!isGif) {
          try {
            const compressedBlob = await imageCompression(
              blob,
              compressionOptions
            );
            processedBlobs.push(compressedBlob);
          } catch (compressionError) {
            console.log(
              "Compression failed, using original:",
              compressionError
            );
            processedBlobs.push(blob);
          }
        } else {
          // For GIFs, check size
          if (blob.size > MAX_FILE_SIZE) {
            console.log("GIF too large, skipping:", blob.name);
            continue;
          }
          processedBlobs.push(blob);
        }
      }

      if (processedBlobs.length > 0) {
        callback({ blobs: processedBlobs });
      } else {
        callback({ error: "No valid files selected" });
      }
    } else {
      callback({ error: "no files" });
    }
  };

  inputEl.oncancel = () => {
    callback({ error: "Upload cancelled" });
  };

  inputEl.onerror = () => {
    callback({ error: "error" });
  };

  inputEl.click();
};

export {
  downloadFile,
  getExtension,
  getUploadedImage,
  getUploadedImages,
  getUploadedImageAsBlob,
  getBlobFromUser,
  processDroppedFile,
};

// Process a dropped file (for drag and drop)
async function processDroppedFile(file, callback) {
  if (!file) {
    callback({ error: "No file provided" });
    return;
  }

  // Check if it's an image
  const validTypes = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/gif",
    "image/svg+xml",
    "image/webp",
  ];
  if (!validTypes.includes(file.type)) {
    callback({
      error: "Please drop an image file (PNG, JPG, GIF, SVG, or WebP)",
    });
    return;
  }

  let blob = file;

  // Check file size - show warning modal if exceeds 3MB
  if (blob.size > MAX_FILE_SIZE) {
    const fileSizeMB = (blob.size / 1024 / 1024).toFixed(1);
    const userChoice = await showFileSizeModal(fileSizeMB);

    if (!userChoice) {
      callback({ error: "Please choose a smaller image." });
      return;
    }
  }

  // Compress image if it's not a GIF
  const isGif = blob.type === "image/gif";

  if (!isGif) {
    try {
      blob = await imageCompression(blob, compressionOptions);
    } catch (compressionError) {
      console.log("Compression failed, using original:", compressionError);
    }
  } else {
    // For GIFs, check size
    if (blob.size > MAX_FILE_SIZE) {
      callback({
        error:
          "GIF file exceeds 3MB limit. GIFs cannot be compressed. Please choose a smaller GIF.",
      });
      return;
    }
  }

  // Convert to data URL and get aspect ratio
  let reader = new FileReader();
  reader.onload = () => {
    let img = new Image();
    img.onload = () => {
      const { naturalWidth, naturalHeight } = img;
      let aspectRatio = naturalWidth / naturalHeight;
      callback({ result: reader.result, aspectRatio });
    };
    img.onerror = () => {
      callback({ error: "Error processing image for moodboard" });
    };
    img.src = reader.result;
  };
  reader.onerror = (e) => {
    console.log(e);
    callback({ error: "Error reading dropped image" });
  };
  reader.readAsDataURL(blob);
}
